import "./Calendario.css";
import React, { useEffect, useMemo, useRef, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import bootstrap5Plugin from "@fullcalendar/bootstrap5";
import esLocale from "@fullcalendar/core/locales/es";
import { Alert, Spinner } from "react-bootstrap";
import {
  FaBirthdayCake,
  FaChevronLeft,
  FaChevronRight,
  FaEnvelope,
  FaRegNewspaper,
  FaRocket,
  FaUser,
} from "react-icons/fa";
import { getApiUrl } from "../../../utils/apiUrl";
import CargandoSpinner from "../../0. Utilidades/loadingSpinner";
import Graficos from "../../0. DashBoard/0. Gráficos/Graficos";
import Workflow from "../../0. DashBoard/5. Workflow/Workflow";

const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const WEEKDAY_KEYS = ["domingo", "lunes", "martes", "miercoles", "jueves", "viernes", "sabado"];

const parseDate = (dateString) => {
  if (!dateString) return null;

  const [year, month, day] = dateString.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const formatDateParam = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const addDays = (date, days) => {
  return new Date(date.getTime() + days * ONE_DAY_MS);
};

const monthStart = (date) => new Date(date.getFullYear(), date.getMonth(), 1);
const addMonths = (date, months) => new Date(date.getFullYear(), date.getMonth() + months, 1);
const weekStart = (date) => {
  const mondayOffset = (date.getDay() + 6) % 7;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() - mondayOffset);
};

const formatWeekTitle = (startDate) => {
  const endDate = addDays(startDate, 6);
  const startLabel = startDate.toLocaleDateString("es-CL", {
    day: "numeric",
    month: "short",
  });
  const endLabel = endDate.toLocaleDateString("es-CL", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  return `${startLabel} – ${endLabel}`;
};

const BIRTHDAY_LABEL = "Cumplea\u00f1os";
const CURRENT_MONTH = monthStart(new Date());
const CALENDAR_START = addMonths(CURRENT_MONTH, -6);
const CALENDAR_END = addMonths(CURRENT_MONTH, 7);
const WEEK_VIEW_BREAKPOINT = 650;
const DASHBOARD_PANELS = [
  { id: "calendar", title: "Calendario Mensual" },
  { id: "statistics", title: "Estadísticas" },
  { id: "workflow", title: "Ruta de la Maestra" },
];
const STATISTICS_TOPICS = [
  { id: "personas", title: "Personas", icon: FaUser },
  { id: "iniciativas", title: "Iniciativas", icon: FaRocket },
  { id: "novedades", title: "Novedades", icon: FaRegNewspaper },
];

const getEventColor = (tipo) => {
  if (tipo === "Cumpleanos") return "#e83e8c";
  return tipo === "Programa" ? "#185b86" : "#49b7d4";
};

const getEventTextColor = () => {
  return "#ffffff";
};

const getEventUrl = (event) => {
  if (event.url) return event.url;
  return `/${event.tipo.toLowerCase()}/${event.id}`;
};

const getEventTitle = (event) => {
  if (event.tipo === "Cumpleanos") {
    return event.nombre;
  }

  return event.comuna ? `${event.nombre} (${event.comuna})` : event.nombre;
};

const buildCalendarEvents = (events) => {
  return events.flatMap((event) => {
    const startDate = parseDate(event.f_inicio);
    const endDate = parseDate(event.f_termino);

    if (!startDate || !endDate) return [];

    const calendarEvents = [];
    let currentDate = startDate;

    while (currentDate <= endDate) {
      const validDays = Array.isArray(event.dias_semana) ? event.dias_semana : [];
      const occursToday = validDays.length === 0 || validDays.includes(WEEKDAY_KEYS[currentDate.getDay()]);

      if (occursToday) {
        calendarEvents.push({
          id: `${event.tipo}-${event.id}-${formatDateParam(currentDate)}`,
          groupId: `${event.tipo}-${event.id}`,
          title: getEventTitle(event),
          start: formatDateParam(currentDate),
          end: formatDateParam(addDays(currentDate, 1)),
          allDay: true,
          url: getEventUrl(event),
          backgroundColor: getEventColor(event.tipo),
          borderColor: getEventColor(event.tipo),
          textColor: getEventTextColor(),
          extendedProps: {
            tipo: event.tipo,
            sortOrder: event.tipo === "Cumpleanos" ? 0 : 1,
            rut: event.rut,
            nombre: event.nombre,
            correo: event.correo,
            celular: event.celular,
            nacimiento: event.nacimiento,
            oficio: event.oficio,
            comuna: event.comuna,
            region: event.region,
            imagen: event.imagen,
          },
        });
      }

      currentDate = addDays(currentDate, 1);
    }

    return calendarEvents;
  });
};

const XLSX_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const escapeExcelValue = (value) => {
  const stringValue = value == null ? "" : String(value);
  return stringValue
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
};

const getColumnName = (index) => {
  let columnName = "";
  let columnNumber = index;

  while (columnNumber > 0) {
    const remainder = (columnNumber - 1) % 26;
    columnName = String.fromCharCode(65 + remainder) + columnName;
    columnNumber = Math.floor((columnNumber - 1) / 26);
  }

  return columnName;
};

const buildBirthdayRows = (birthdayEvents) => {
  return birthdayEvents.map((event) => [
    event.rut,
    event.nombre,
    event.correo,
    event.celular,
    event.nacimiento,
    event.f_inicio,
    event.oficio,
    event.comuna,
    event.region,
  ]);
};

const buildExcelWorksheet = (rows) => {
  const headers = [
    "RUT",
    "Nombre",
    "Correo",
    "Celular",
    "Nacimiento",
    "Cumpleanos",
    "Oficio",
    "Comuna",
    "Region",
  ];
  const sheetRows = [headers, ...rows]
    .map((row, rowIndex) => {
      const excelRowNumber = rowIndex + 1;
      const cells = row
        .map((cell, cellIndex) => {
          const cellReference = `${getColumnName(cellIndex + 1)}${excelRowNumber}`;
          return `<c r="${cellReference}" t="inlineStr"><is><t>${escapeExcelValue(
            cell,
          )}</t></is></c>`;
        })
        .join("");

      return `<row r="${excelRowNumber}">${cells}</row>`;
    })
    .join("");

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <sheetData>${sheetRows}</sheetData>
</worksheet>`;
};

const CRC_TABLE = (() => {
  const table = [];

  for (let index = 0; index < 256; index += 1) {
    let crc = index;

    for (let bit = 0; bit < 8; bit += 1) {
      crc = crc & 1 ? 0xedb88320 ^ (crc >>> 1) : crc >>> 1;
    }

    table[index] = crc >>> 0;
  }

  return table;
})();

const calculateCrc32 = (bytes) => {
  let crc = 0xffffffff;

  for (let index = 0; index < bytes.length; index += 1) {
    crc = CRC_TABLE[(crc ^ bytes[index]) & 0xff] ^ (crc >>> 8);
  }

  return (crc ^ 0xffffffff) >>> 0;
};

const writeUint16 = (view, offset, value) => {
  view.setUint16(offset, value, true);
};

const writeUint32 = (view, offset, value) => {
  view.setUint32(offset, value, true);
};

const getZipDateParts = () => {
  const nowDate = new Date();
  const dosTime =
    (nowDate.getHours() << 11) |
    (nowDate.getMinutes() << 5) |
    Math.floor(nowDate.getSeconds() / 2);
  const dosDate =
    ((nowDate.getFullYear() - 1980) << 9) |
    ((nowDate.getMonth() + 1) << 5) |
    nowDate.getDate();

  return { dosTime, dosDate };
};

const concatUint8Arrays = (arrays) => {
  const totalLength = arrays.reduce((sum, array) => sum + array.length, 0);
  const result = new Uint8Array(totalLength);
  let offset = 0;

  arrays.forEach((array) => {
    result.set(array, offset);
    offset += array.length;
  });

  return result;
};

const createZipBlob = (files) => {
  const encoder = new TextEncoder();
  const { dosTime, dosDate } = getZipDateParts();
  const localParts = [];
  const centralParts = [];
  const entries = Object.entries(files);
  let currentOffset = 0;

  entries.forEach(([fileName, content]) => {
    const fileNameBytes = encoder.encode(fileName);
    const fileBytes = encoder.encode(content);
    const crc32 = calculateCrc32(fileBytes);
    const localHeader = new Uint8Array(30 + fileNameBytes.length);
    const localView = new DataView(localHeader.buffer);

    writeUint32(localView, 0, 0x04034b50);
    writeUint16(localView, 4, 20);
    writeUint16(localView, 6, 0);
    writeUint16(localView, 8, 0);
    writeUint16(localView, 10, dosTime);
    writeUint16(localView, 12, dosDate);
    writeUint32(localView, 14, crc32);
    writeUint32(localView, 18, fileBytes.length);
    writeUint32(localView, 22, fileBytes.length);
    writeUint16(localView, 26, fileNameBytes.length);
    writeUint16(localView, 28, 0);
    localHeader.set(fileNameBytes, 30);

    localParts.push(localHeader, fileBytes);

    const centralHeader = new Uint8Array(46 + fileNameBytes.length);
    const centralView = new DataView(centralHeader.buffer);

    writeUint32(centralView, 0, 0x02014b50);
    writeUint16(centralView, 4, 20);
    writeUint16(centralView, 6, 20);
    writeUint16(centralView, 8, 0);
    writeUint16(centralView, 10, 0);
    writeUint16(centralView, 12, dosTime);
    writeUint16(centralView, 14, dosDate);
    writeUint32(centralView, 16, crc32);
    writeUint32(centralView, 20, fileBytes.length);
    writeUint32(centralView, 24, fileBytes.length);
    writeUint16(centralView, 28, fileNameBytes.length);
    writeUint16(centralView, 30, 0);
    writeUint16(centralView, 32, 0);
    writeUint16(centralView, 34, 0);
    writeUint16(centralView, 36, 0);
    writeUint32(centralView, 38, 0);
    writeUint32(centralView, 42, currentOffset);
    centralHeader.set(fileNameBytes, 46);

    centralParts.push(centralHeader);
    currentOffset += localHeader.length + fileBytes.length;
  });

  const centralDirectory = concatUint8Arrays(centralParts);
  const endRecord = new Uint8Array(22);
  const endView = new DataView(endRecord.buffer);

  writeUint32(endView, 0, 0x06054b50);
  writeUint16(endView, 4, 0);
  writeUint16(endView, 6, 0);
  writeUint16(endView, 8, entries.length);
  writeUint16(endView, 10, entries.length);
  writeUint32(endView, 12, centralDirectory.length);
  writeUint32(endView, 16, currentOffset);
  writeUint16(endView, 20, 0);

  const zipContent = concatUint8Arrays([
    ...localParts,
    centralDirectory,
    endRecord,
  ]);

  return new Blob([zipContent], { type: XLSX_MIME });
};

const buildBirthdayExcel = (currentMonthBirthdays, nextMonthBirthdays) => {
  const worksheets = [
    {
      id: 1,
      name: "Mes actual",
      xml: buildExcelWorksheet(buildBirthdayRows(currentMonthBirthdays)),
    },
    {
      id: 2,
      name: "Proximo mes",
      xml: buildExcelWorksheet(buildBirthdayRows(nextMonthBirthdays)),
    },
  ];

  const workbookSheets = worksheets
    .map(
      (sheet) =>
        `<sheet name="${escapeExcelValue(sheet.name)}" sheetId="${sheet.id}" r:id="rId${sheet.id}"/>`,
    )
    .join("");
  const workbookRelationships = worksheets
    .map(
      (sheet) =>
        `<Relationship Id="rId${sheet.id}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${sheet.id}.xml"/>`,
    )
    .join("");
  const worksheetOverrides = worksheets
    .map(
      (sheet) =>
        `<Override PartName="/xl/worksheets/sheet${sheet.id}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`,
    )
    .join("");

  const files = {
    "[Content_Types].xml": `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  ${worksheetOverrides}
</Types>`,
    "_rels/.rels": `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`,
    "xl/workbook.xml": `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <sheets>${workbookSheets}</sheets>
</workbook>`,
    "xl/_rels/workbook.xml.rels": `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  ${workbookRelationships}
</Relationships>`,
  };

  worksheets.forEach((sheet) => {
    files[`xl/worksheets/sheet${sheet.id}.xml`] = sheet.xml;
  });

  return createZipBlob(files);
};

const Calendario = () => {
  const [activePanelIndex, setActivePanelIndex] = useState(0);
  const [statisticsTopic, setStatisticsTopic] = useState("personas");
  const [events, setEvents] = useState([]);
  const [birthdayExportEvents, setBirthdayExportEvents] = useState([]);
  const [nextMonthBirthdayEvents, setNextMonthBirthdayEvents] = useState([]);
  const [showBirthdays, setShowBirthdays] = useState(false);
  const [viewDate, setViewDate] = useState(CURRENT_MONTH);
  const [mobileWeekStart, setMobileWeekStart] = useState(() => weekStart(new Date()));
  const [visibleRange, setVisibleRange] = useState({
    start: formatDateParam(CURRENT_MONTH),
    end: formatDateParam(addDays(addMonths(CURRENT_MONTH, 1), -1)),
  });
  const [isWeeklyView, setIsWeeklyView] = useState(() => (
    typeof window !== "undefined"
    && window.matchMedia(`(max-width: ${WEEK_VIEW_BREAKPOINT - 1}px)`).matches
  ));
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const loadedOnce = useRef(false);
  const calendarRef = useRef(null);

  const visibleEvents = useMemo(() => {
    if (showBirthdays) return events;
    return events.filter((event) => event.tipo !== "Cumpleanos");
  }, [events, showBirthdays]);
  const calendarEvents = useMemo(() => buildCalendarEvents(visibleEvents), [visibleEvents]);
  const mobileWeekDays = useMemo(() => (
    Array.from({ length: 7 }, (_, index) => {
      const day = addDays(mobileWeekStart, index);
      const dateKey = formatDateParam(day);
      return {
        day,
        dateKey,
        events: calendarEvents
          .filter((event) => event.start === dateKey)
          .sort((eventA, eventB) => {
            const orderDifference =
              eventA.extendedProps.sortOrder - eventB.extendedProps.sortOrder;
            return orderDifference || eventA.title.localeCompare(eventB.title);
          }),
      };
    })
  ), [calendarEvents, mobileWeekStart]);
  const birthdayEvents = useMemo(
    () => events.filter((event) => event.tipo === "Cumpleanos"),
    [events],
  );
  const hasBirthdayExport =
    birthdayExportEvents.length > 0 || nextMonthBirthdayEvents.length > 0;

  useEffect(() => {
    const mediaQuery = window.matchMedia(
      `(max-width: ${WEEK_VIEW_BREAKPOINT - 1}px)`,
    );
    const handleViewportChange = (event) => {
      if (event.matches) {
        const calendarDate = calendarRef.current?.getApi().getDate();
        setMobileWeekStart(weekStart(calendarDate || new Date()));
      }
      setIsWeeklyView(event.matches);
    };

    setIsWeeklyView(mediaQuery.matches);
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleViewportChange);
      return () => mediaQuery.removeEventListener("change", handleViewportChange);
    }
    mediaQuery.addListener(handleViewportChange);
    return () => mediaQuery.removeListener(handleViewportChange);
  }, []);

  useEffect(() => {
    if (!isWeeklyView) return;

    const rangeEnd = addDays(mobileWeekStart, 6);
    setVisibleRange({
      start: formatDateParam(mobileWeekStart),
      end: formatDateParam(rangeEnd),
    });
    setViewDate(monthStart(addDays(mobileWeekStart, 3)));
  }, [isWeeklyView, mobileWeekStart]);

  useEffect(() => {
    const controller = new AbortController();
    let activeRequest = true;

    const fetchData = async () => {
      setError("");
      if (loadedOnce.current) setRefreshing(true);

      try {
        const apiUrl = getApiUrl(
          `api/calendario/?date=${formatDateParam(viewDate)}`
          + `&start=${visibleRange.start}&end=${visibleRange.end}`,
        );
        const response = await fetch(apiUrl, {
          credentials: "include",
          cache: "no-store",
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Error: ${response.statusText}`);
        }

        const data = await response.json();
        if (!activeRequest) return;
        setEvents(data.events || []);
        setBirthdayExportEvents(
          data.birthday_export_current
          || (data.events || []).filter((event) => event.tipo === "Cumpleanos"),
        );
        setNextMonthBirthdayEvents(data.next_month_birthdays || []);
      } catch (error) {
        if (error.name === "AbortError") return;
        console.error("Error fetching calendar data:", error);
        if (activeRequest) setError("No se pudo cargar el calendario.");
      } finally {
        if (activeRequest) {
          loadedOnce.current = true;
          setLoading(false);
          setRefreshing(false);
        }
      }
    };

    fetchData();
    return () => {
      activeRequest = false;
      controller.abort();
    };
  }, [viewDate, visibleRange.end, visibleRange.start]);

  useEffect(() => {
    if (loading || isWeeklyView || !calendarRef.current) return;
    const calendarApi = calendarRef.current.getApi();
    if (calendarApi.view.type !== "dayGridMonth") {
      calendarApi.changeView("dayGridMonth");
    }
  }, [isWeeklyView, loading]);

  const changeWeek = (direction) => {
    setMobileWeekStart((currentWeek) => addDays(currentWeek, direction * 7));
  };

  const handleDownloadBirthdays = () => {
    if (!hasBirthdayExport) return;

    const excelBlob = buildBirthdayExcel(
      birthdayExportEvents,
      nextMonthBirthdayEvents,
    );
    const url = URL.createObjectURL(excelBlob);
    const link = document.createElement("a");

    link.href = url;
    link.download = `cumpleanos_maestras_whatsapp_${formatDateParam(viewDate).slice(0, 7)}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const activePanel = DASHBOARD_PANELS[activePanelIndex];
  const showStatistics = activePanel.id === "statistics";
  const showWorkflow = activePanel.id === "workflow";
  const changePanel = (direction) => {
    setActivePanelIndex((currentIndex) => (
      (currentIndex + direction + DASHBOARD_PANELS.length)
      % DASHBOARD_PANELS.length
    ));
  };
  const previousPanel = DASHBOARD_PANELS[
    (activePanelIndex - 1 + DASHBOARD_PANELS.length) % DASHBOARD_PANELS.length
  ];
  const nextPanel = DASHBOARD_PANELS[
    (activePanelIndex + 1) % DASHBOARD_PANELS.length
  ];

  return (
    <div id="calendario">
      <div className="calendario-header">
        <div className="dashboard-panel-heading">
          <button
            type="button"
            className="dashboard-panel-arrow"
            onClick={() => changePanel(-1)}
            aria-label={`Mostrar ${previousPanel.title}`}
            title={previousPanel.title}
          >
            <FaChevronLeft />
          </button>
          <h3>
            {activePanel.id === "calendar" && isWeeklyView
              ? "Calendario Semanal"
              : activePanel.title}
          </h3>
          <button
            type="button"
            className="dashboard-panel-arrow"
            onClick={() => changePanel(1)}
            aria-label={`Mostrar ${nextPanel.title}`}
            title={nextPanel.title}
          >
            <FaChevronRight />
          </button>
        </div>

        {activePanel.id === "statistics" && (
          <div className="calendario-controls" aria-label="Tema de estadísticas">
            {STATISTICS_TOPICS.map((topic) => {
              const TopicIcon = topic.icon;
              const active = statisticsTopic === topic.id;
              return (
                <button
                  key={topic.id}
                  type="button"
                  className={`btn btn-sm calendario-icon-button ${
                    active ? "btn-dark active" : "btn-outline-dark"
                  }`}
                  onClick={() => setStatisticsTopic(topic.id)}
                  title={`Mostrar estadísticas de ${topic.title}`}
                  aria-label={`Mostrar estadísticas de ${topic.title}`}
                  aria-pressed={active}
                >
                  <TopicIcon />
                </button>
              );
            })}
          </div>
        )}

        {activePanel.id === "calendar" && <div className="calendario-controls">
          <button
            type="button"
            className={`btn btn-sm calendario-icon-button ${
              showBirthdays ? "btn-dark active" : "btn-outline-dark"
            }`}
            onClick={() => setShowBirthdays((currentValue) => !currentValue)}
            disabled={!birthdayEvents.length}
            title={
              showBirthdays
                ? "Ocultar cumpleaños de Maestras con WhatsApp"
                : "Mostrar cumpleaños de Maestras con WhatsApp"
            }
            aria-label={
              showBirthdays
                ? "Ocultar cumpleaños de Maestras con WhatsApp"
                : "Mostrar cumpleaños de Maestras con WhatsApp"
            }
            aria-pressed={showBirthdays}
          >
            <FaBirthdayCake />
          </button>

          <button
            type="button"
            className="btn btn-sm btn-outline-dark calendario-icon-button"
            onClick={handleDownloadBirthdays}
            disabled={!hasBirthdayExport}
            title="Descargar cumpleaños de Maestras con WhatsApp"
            aria-label="Descargar cumpleaños de Maestras con WhatsApp"
          >
            <FaEnvelope />
          </button>
        </div>}
      </div>

      {showStatistics ? (
        <div className="statistics-panel-container">
          <Graficos topic={statisticsTopic} />
        </div>
      ) : showWorkflow ? (
        <div className="workflow-panel-container">
          <Workflow />
        </div>
      ) : (
        <>
          {error && <Alert variant="danger">{error}</Alert>}

          {loading ? (
            <div className="calendario-loading">
              <CargandoSpinner />
            </div>
          ) : (
            <div className="calendario-container">
              {isWeeklyView ? (
                <div className="mobile-week-calendar">
                  <div className="mobile-week-toolbar">
                    <button
                      type="button"
                      onClick={() => changeWeek(-1)}
                      disabled={addDays(mobileWeekStart, -7) < CALENDAR_START}
                      aria-label="Semana anterior"
                    >
                      ‹
                    </button>
                    <strong>{formatWeekTitle(mobileWeekStart)}</strong>
                    <button
                      type="button"
                      onClick={() => changeWeek(1)}
                      disabled={addDays(mobileWeekStart, 7) >= CALENDAR_END}
                      aria-label="Semana siguiente"
                    >
                      ›
                    </button>
                  </div>

                  <div className="mobile-week-days">
                    {mobileWeekDays.map(({ day, dateKey, events: dayEvents }) => {
                      const isToday = dateKey === formatDateParam(new Date());
                      return (
                        <section
                          className={`mobile-week-day${isToday ? " is-today" : ""}`}
                          key={dateKey}
                        >
                          <div className="mobile-week-day-label">
                            <span>{day.toLocaleDateString("es-CL", { weekday: "long" })}</span>
                            <strong>{day.toLocaleDateString("es-CL", { day: "numeric", month: "short" })}</strong>
                          </div>
                          <div className="mobile-week-day-events">
                            {dayEvents.length ? dayEvents.map((event) => (
                              <button
                                type="button"
                                className="mobile-week-event"
                                key={event.id}
                                style={{
                                  backgroundColor: event.backgroundColor,
                                  borderColor: event.borderColor,
                                  color: event.textColor,
                                }}
                                onClick={() => { window.location.href = event.url; }}
                              >
                                {event.title}
                              </button>
                            )) : (
                              <span className="mobile-week-empty">Sin iniciativas</span>
                            )}
                          </div>
                        </section>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <FullCalendar
                  ref={calendarRef}
                  plugins={[dayGridPlugin, bootstrap5Plugin]}
                  themeSystem="bootstrap5"
                  initialView="dayGridMonth"
                  initialDate={formatDateParam(viewDate)}
                  locale={esLocale}
                  buttonIcons={false}
                  buttonText={{ prev: "‹", next: "›" }}
                  headerToolbar={{ left: "prev", center: "title", right: "next" }}
                  validRange={{
                    start: formatDateParam(CALENDAR_START),
                    end: formatDateParam(CALENDAR_END),
                  }}
                  datesSet={(dateInfo) => {
                    const rangeStart = formatDateParam(dateInfo.start);
                    const rangeEnd = formatDateParam(addDays(dateInfo.end, -1));
                    setVisibleRange((currentRange) => (
                      currentRange.start === rangeStart && currentRange.end === rangeEnd
                        ? currentRange
                        : { start: rangeStart, end: rangeEnd }
                    ));

                    const nextDate = monthStart(dateInfo.view.currentStart);
                    if (
                      nextDate.getFullYear() !== viewDate.getFullYear()
                      || nextDate.getMonth() !== viewDate.getMonth()
                    ) {
                      setViewDate(nextDate);
                    }
                  }}
                  events={calendarEvents}
                  height="auto"
                  firstDay={1}
                  fixedWeekCount={false}
                  nowIndicator
                  eventDisplay="block"
                  eventOrder={(eventA, eventB) => {
                    const orderA = eventA.extendedProps.sortOrder;
                    const orderB = eventB.extendedProps.sortOrder;

                    if (orderA !== orderB) {
                      return orderA - orderB;
                    }

                    return eventA.title.localeCompare(eventB.title);
                  }}
                  eventDidMount={(info) => {
                    const { tipo, nombre, comuna } = info.event.extendedProps;
                    const title =
                      tipo === "Cumpleanos"
                        ? `${BIRTHDAY_LABEL}: ${nombre}`
                        : `${tipo}: ${nombre} (${comuna})`;
                    info.el.setAttribute("title", title);
                  }}
                  eventClick={(info) => {
                    if (info.event.url) {
                      info.jsEvent.preventDefault();
                      window.location.href = info.event.url;
                    }
                  }}
                />
              )}
              {refreshing && (
                <div className="calendario-refreshing" role="status" aria-live="polite">
                  <Spinner animation="border" />
                  <span>{isWeeklyView ? "Cargando semana…" : "Cargando mes…"}</span>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Calendario;
